(function () {
  'use strict';

  const fulfillmentConstants = window.FulfillmentConstants;
  if (!fulfillmentConstants) {
    console.error('LocationCatalogPricing: FulfillmentConstants required');
    return;
  }

  function hasCatalogPricingContext() {
    try {
      const date = localStorage.getItem(fulfillmentConstants.DATE);
      const locId = localStorage.getItem(fulfillmentConstants.LOCATION_ID);
      const type = localStorage.getItem(fulfillmentConstants.DELIVERY_TYPE);
      const zip = localStorage.getItem(fulfillmentConstants.ZIP_CODE);
      const draftMode = !!(window.FulfillmentState && typeof window.FulfillmentState.isDraftMode === 'function' && window.FulfillmentState.isDraftMode());
      if (draftMode) return false;
      return !!(date && locId && type && zip);
    } catch (e) {
      return false;
    }
  }

  function setBodyReadyClass(ready) {
    const body = document.body;
    if (!body) return;
    body.classList.toggle('location-not-ready', !ready);
  }

  function normalizeTimestamp(value) {
    if (value == null) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const numeric = Number(value);
      if (!isNaN(numeric)) return numeric;
      const parsed = Date.parse(value);
      return isNaN(parsed) ? 0 : parsed;
    }
    return 0;
  }

  function normalizeLocationId(value) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeLocationId === 'function') {
      return window.EtInventoryDomain.normalizeLocationId(value);
    }
    const raw = (value == null ? '' : String(value)).trim();
    if (!raw) return '';
    if (raw.indexOf('gid://shopify/Location/') !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  function normalizeEntityId(value) {
    if (window.EtInventoryDomain && typeof window.EtInventoryDomain.normalizeEntityId === 'function') {
      return window.EtInventoryDomain.normalizeEntityId(value);
    }
    const raw = (value == null ? '' : String(value)).trim();
    if (!raw) return '';
    if (raw.indexOf('gid://') !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  function extractAmount(entry) {
    if (!entry) return null;
    const direct = entry.amount != null ? entry.amount : (entry.value != null ? entry.value : null);
    if (direct != null) return direct;
    if (entry.price != null) {
      if (typeof entry.price === 'number' || typeof entry.price === 'string') return entry.price;
      if (entry.price.amount != null) return entry.price.amount;
      if (entry.price.value != null) return entry.price.value;
    }
    if (entry.unit_price != null) return entry.unit_price;
    if (entry.unitPrice != null) return entry.unitPrice;
    return null;
  }

  function extractCatalogId(entry) {
    if (!entry) return '';
    return normalizeEntityId(
      entry.catalog_id ||
      entry.catalogId ||
      (entry.catalog && (entry.catalog.id || entry.catalog.catalogId)) ||
      entry.catalog
    );
  }

  function extractVariantId(entry) {
    if (!entry) return '';
    return normalizeEntityId(
      entry.variant_id ||
      entry.variantId ||
      entry.variantGid ||
      (entry.variant && (entry.variant.id || entry.variant.variantId || entry.variant.variant_id)) ||
      (entry.merchandise && entry.merchandise.id) ||
      entry.variant
    );
  }

  function normalizePriceEntries(rawEntries) {
    if (!rawEntries) return [];
    if (Array.isArray(rawEntries)) return rawEntries;
    if (rawEntries && Array.isArray(rawEntries.edges)) {
      return rawEntries.edges.map(function (edge) { return edge && edge.node ? edge.node : edge; }).filter(Boolean);
    }
    if (rawEntries && Array.isArray(rawEntries.nodes)) {
      return rawEntries.nodes;
    }
    if (rawEntries && Array.isArray(rawEntries.items)) {
      return rawEntries.items;
    }
    if (typeof rawEntries === 'object') {
      return Object.keys(rawEntries).map(function (k) {
        let value = rawEntries[k];
        if (value && typeof value === 'object') {
          if (!value.variantId && !value.variant_id && !(value.variant && value.variant.id)) {
            value = Object.assign({ variantId: k }, value);
          }
          return value;
        }
        return { variantId: k, price: value };
      });
    }
    return [];
  }

  function getCatalogPrices() {
    const priceByCatalogId = {};
    const priceByVariantId = {};
    try {
      const keys = Object.keys(localStorage);
      const catalogKeys = keys.filter(function (key) { return key.indexOf(fulfillmentConstants.CATALOG_KEY_PREFIX) === 0; });
      if (catalogKeys.length === 0) return { priceByCatalogId: priceByCatalogId, priceByVariantId: priceByVariantId };
      const selectedLocId = normalizeLocationId(localStorage.getItem(fulfillmentConstants.LOCATION_ID) || '');
      const keyPrefix = fulfillmentConstants.CATALOG_KEY_PREFIX + '-';
      const candidates = catalogKeys.map(function (k) {
        const raw = localStorage.getItem(k);
        let parsed = null;
        try { parsed = raw ? JSON.parse(raw) : null; } catch (e) { return null; }
        if (!parsed) return null;
        const ts = (parsed.updatedAt != null ? parsed.updatedAt : parsed.timestamp) ||
          (parsed.data && (parsed.data.updatedAt != null ? parsed.data.updatedAt : parsed.data.timestamp));
        const keyLocId = k.indexOf(keyPrefix) === 0 ? normalizeLocationId(k.slice(keyPrefix.length)) : '';
        const payloadLocId = normalizeLocationId(parsed.locationId || (parsed.data && parsed.data.locationId) || '');
        return { key: k, raw: raw, parsed: parsed, ts: normalizeTimestamp(ts), keyLocId: keyLocId || payloadLocId };
      }).filter(Boolean);
      const candidatesForLocation = selectedLocId
        ? candidates.filter(function (c) { return c.keyLocId === selectedLocId; })
        : candidates;
      if (selectedLocId && candidatesForLocation.length === 0) {
        return { priceByCatalogId: priceByCatalogId, priceByVariantId: priceByVariantId };
      }
      let best = candidatesForLocation.reduce(function (a, b) {
        if (!a) return b;
        return (a.ts >= b.ts) ? a : b;
      }, null);
      if (!best) best = { key: catalogKeys.sort().pop(), raw: null, parsed: null };
      if (!best.raw) best.raw = localStorage.getItem(best.key);
      if (!best.parsed && best.raw) try { best.parsed = JSON.parse(best.raw); } catch (e) {}
      const parsed = best.parsed;
      if (parsed) {
        const priceEntries = normalizePriceEntries(
          (parsed.data && parsed.data.catalog && parsed.data.catalog.prices) ||
          (parsed.catalog && parsed.catalog.prices) ||
          (parsed.data && parsed.data.prices) ||
          parsed.prices
        );
        for (let i = 0; i < priceEntries.length; i++) {
          const entry = priceEntries[i];
          const catalogId = extractCatalogId(entry);
          const amount = extractAmount(entry);
          const variantId = extractVariantId(entry);
          if (amount != null) {
            const numericAmount = parseFloat(amount);
            if (isNaN(numericAmount)) continue;
            const formatted = numericAmount.toFixed(2);
            if (catalogId) priceByCatalogId[String(catalogId)] = formatted;
            if (variantId) priceByVariantId[variantId] = formatted;
          }
        }
      }
    } catch (e) {}
    return { priceByCatalogId: priceByCatalogId, priceByVariantId: priceByVariantId };
  }

  function formatCatalogMoney(amount) {
    const numericAmount = typeof amount === 'number' ? amount : parseFloat(amount);
    if (isNaN(numericAmount)) return '';

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(Math.round(numericAmount * 100));
      } catch (e) {}
    }

    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(numericAmount);
    } catch (e) {
      return '$' + numericAmount.toFixed(2);
    }
  }

  function isB2BCustomer() {
    return document.body.getAttribute('data-b2b-customer') === 'true';
  }

  function applyCatalogPricing() {
    if (isB2BCustomer()) {
      document.body.classList.remove('location-not-ready');
      return;
    }

    const ready = hasCatalogPricingContext();
    setBodyReadyClass(ready);

    const gatedContainers = document.querySelectorAll('[data-variant-catalog-id]');
    if (!ready) {
      gatedContainers.forEach(function (container) {
        container.classList.add('hidden');
        container.style.display = 'none';
        const priceWrapper = container.querySelector('.price, .price-block');
        if (priceWrapper) {
          priceWrapper.classList.add('hidden');
          priceWrapper.style.display = 'none';
        }
      });
      return;
    }

    const prices = getCatalogPrices();
    const priceByCatalogId = prices.priceByCatalogId;
    const priceByVariantId = prices.priceByVariantId;

    gatedContainers.forEach(function (container) {
      const catalogId = container.getAttribute('data-variant-catalog-id');
      if (!catalogId) return;

      const normalizedCatalogId = normalizeEntityId(catalogId);
      const variantId = normalizeEntityId(container.getAttribute('data-variant-id'));
      const price = priceByCatalogId[normalizedCatalogId] || (variantId && priceByVariantId[variantId]);

      if (!price) {
        container.classList.add('hidden');
        container.style.display = 'none';
        const hideWrapper = container.querySelector('.price, .price-block');
        if (hideWrapper) {
          hideWrapper.classList.add('hidden');
          hideWrapper.style.display = 'none';
        }
        return;
      }

      container.classList.remove('hidden');
      container.style.display = '';
      const priceWrapper = container.querySelector('.price, .price-block');
      if (priceWrapper) {
        priceWrapper.classList.remove('hidden');
        priceWrapper.style.display = '';
      }

      const formattedPrice = formatCatalogMoney(price);
      if (!formattedPrice) return;

      const priceEls = container.querySelectorAll('.price-item--regular, .price-item--sale, .price-item');
      priceEls.forEach(function (priceEl) {
        priceEl.textContent = formattedPrice;
      });
    });

    if (typeof window.updateRechargeCardDiscounts === 'function') {
      window.updateRechargeCardDiscounts();
    }
  }

  function cleanupStaleCatalogCache() {
    try {
      const keys = Object.keys(localStorage).filter(function (key) {
        return key.indexOf(fulfillmentConstants.CATALOG_KEY_PREFIX) === 0;
      });
      if (keys.length <= 10) return;
      const entries = [];
      for (let i = 0; i < keys.length; i++) {
        try {
          const raw = localStorage.getItem(keys[i]);
          const parsed = raw ? JSON.parse(raw) : null;
          const ts = (parsed && (parsed.updatedAt != null ? parsed.updatedAt : parsed.timestamp)) ||
            (parsed && parsed.data && (parsed.data.updatedAt != null ? parsed.data.updatedAt : parsed.data.timestamp)) || 0;
          entries.push({ key: keys[i], ts: normalizeTimestamp(ts) });
        } catch (e) {}
      }
      entries.sort(function (a, b) { return a.ts - b.ts; });
      const toRemove = Math.max(0, entries.length - 10);
      for (let j = 0; j < toRemove; j++) {
        localStorage.removeItem(entries[j].key);
      }
    } catch (e) {}
  }

  let isCatalogPricingInitialized = false;

  function init() {
    if (isCatalogPricingInitialized) return;
    isCatalogPricingInitialized = true;
    cleanupStaleCatalogCache();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', applyCatalogPricing);
    } else {
      applyCatalogPricing();
    }
    document.addEventListener('variant:change', applyCatalogPricing);
    window.addEventListener('variantFilterComplete', applyCatalogPricing);
    window.addEventListener('locationPricesReady', applyCatalogPricing);
    window.addEventListener('fulfillmentDateChanged', applyCatalogPricing);
    window.addEventListener('locationChanged', applyCatalogPricing);
  }

  const LocationCatalogPricing = {
    refresh: applyCatalogPricing,
    isLocationReady: hasCatalogPricingContext,
    init: init
  };

  window.LocationCatalogPricing = LocationCatalogPricing;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
